import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import BackendStatus from '../components/BackendStatus';
import VerseOfTheDay from '../components/VerseOfTheDay';
import HomeActions from '../components/HomeActions';
import WelcomeBackSection from '../components/WelcomeBackSection';
import authService from '../services/authService';
import '../components/ShareWithFriends.css';
// No longer need game setting icons
import {
  FacebookIcon,
  TwitterIcon,
  WhatsAppIcon,
  EmailIcon,
  LinkIcon
} from '../components/icons/SocialIcons';

const HomePage: React.FC = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userProfile, setUserProfile] = useState<any>(null);

  const [copySuccess, setCopySuccess] = useState(false);
  const [activeShareButton, setActiveShareButton] = useState<string | null>(null);



  // Ensure backend login and fetch user profile
  useEffect(() => {
    const setupAuth = async () => {
      if (isAuthenticated && user) {
        try {
          console.log('🔐 Setting up authentication for user:', user.email);
          console.log('🔍 Auth0 user object:', {
            sub: user.sub,
            email: user.email,
            name: user.name,
            nickname: user.nickname,
            picture: user.picture
          });

          // Get Auth0 access token
          const accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'openid profile email'
            }
          });

          console.log('✅ Got access token, proceeding with backend sync');

          // Always attempt backend sync with Auth0 - don't rely on sessionStorage
          // SessionStorage might contain stale data from previous sessions
          console.log('👤 Attempting backend sync with Auth0');
          try {
            await authService.loginWithAuth0(user, accessToken);
            console.log('✅ Auth0 login successful');
          } catch (loginError) {
            console.error('❌ Auth0 login failed:', loginError);
            throw loginError;
          }

          // Fetch user profile
          console.log('📋 Fetching user profile');
          const profile = await authService.getProfile();
          setUserProfile(profile);
          console.log('✅ User profile loaded successfully');

        } catch (error) {
          console.error('❌ Failed to setup auth:', error);

          // Clear session data if we encounter auth issues to start fresh
          if (error instanceof Error) {
            if (error.message.includes('User profile not found') ||
                error.message.includes('Failed to sync user data') ||
                error.message.includes('Request failed with status code 404')) {
              console.log('🔄 Clearing session data due to auth issues');
              authService.clearSession();
            }
          }

          // Provide more specific error messages
          if (error instanceof Error) {
            if (error.message.includes('Failed to sync user data')) {
              console.warn('⚠️ User sync failed, but authentication may still work');
            } else if (error.message.includes('Request failed with status code 404')) {
              console.warn('⚠️ User profile not found, this may be a new user');
            } else {
              console.error('💥 Unexpected authentication error:', error.message);
            }
          }

          // Don't block the UI - the user can still use basic features
          // The app will show a welcome message for unauthenticated users
        }
      } else {
        console.log('ℹ️ User not authenticated yet or user data not available');
      }
    };

    setupAuth();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  
  
  
  const handleShare = (platform: string) => {
    const shareText = "Test your Bible knowledge with KNOW, an engaging trivia game!";
    const shareUrl = window.location.href;
    
    // Add visual feedback by setting active button
    setActiveShareButton(platform);
    setTimeout(() => setActiveShareButton(null), 800);
    
    switch(platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=Check out this Bible Trivia Game&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl).then(() => {
          setCopySuccess(true);
          // Add animation style
          setTimeout(() => setCopySuccess(false), 2000);
        });
        break;
      default:
        break;
    }
  };


  return (
    <div className="min-h-screen flex flex-col items-center py-8 px-4 home-page-bg">
      {/* Animated background elements */}
      <div className="floating-scripture floating-scripture-1">
        <span>✝️</span>
      </div>
      <div className="floating-scripture floating-scripture-2">
        <span>📖</span>
      </div>
      <div className="floating-scripture floating-scripture-3">
        <span>✨</span>
      </div>
      <div className="floating-scripture floating-scripture-4">
        <span>🕊️</span>
      </div>
      <div className="floating-scripture floating-scripture-5">
        <span>📜</span>
      </div>
      
      {/* Light beams */}
      <div className="light-beam light-beam-1"></div>
      <div className="light-beam light-beam-2"></div>
      <div className="light-beam light-beam-3"></div>
      
      {/* Particles */}
      <div className="particles-container">
        {[...Array(20)].map((_, index) => (
          <div key={index} className={`particle particle-${index + 1}`}></div>
        ))}
      </div>
      
      <div className="container">
        {/* User Welcome Section */}
        {isAuthenticated && userProfile && (
          <WelcomeBackSection 
            user={userProfile}
            userEmail={user?.email || ''}
          />
        )}
        
        {/* Side by side layout */}
        <div className="home-grid fade-in">
          <VerseOfTheDay />
          <HomeActions />
        </div>
        
        
        {/* Biblical Scenarios Feature Banner */}
        <div className="scenarios-feature-banner fade-in delay-50">
          <div className="scenarios-banner-content">
            <div className="scenarios-banner-icon">🎭</div>
            <div className="scenarios-banner-text">
              <h3>NEW! Biblical Scenarios</h3>
              <p>Experience biblical decision-making! What would YOU do in these scriptural situations?</p>
            </div>
            <Link
              to="/scenario-game"
              className="scenarios-banner-button"
            >
              Try Now
            </Link>
          </div>
        </div>

        {/* Walk the Story Feature Banner */}
        <div className="walk-story-feature-banner fade-in delay-75">
          <div className="walk-story-banner-content">
            <div className="walk-story-banner-icon">👣</div>
            <div className="walk-story-banner-text">
              <h3>NEW! Walk the Story</h3>
              <p>Experience biblical narratives through interactive conversations with key figures!</p>
            </div>
            <Link
              to="/walk-the-story"
              className="walk-story-banner-button"
            >
              Play Now
            </Link>
          </div>
        </div>
        
        {/* Timeline Feature Banner */}
        <div className="timeline-feature-banner fade-in delay-100">
          <div className="timeline-banner-content">
            <div className="timeline-banner-icon">📜</div>
            <div className="timeline-banner-text">
              <h3>NEW! Biblical Timeline</h3>
              <p>Journey through biblical history! Explore God's story through interactive events.</p>
              <div className="timeline-coming-soon">
                <span className="coming-soon-badge">Coming Soon</span>
              </div>
            </div>
            <div className="timeline-banner-button disabled">
              Coming Soon
            </div>
          </div>
        </div>
        
        {/* Visual divider */}
        <div className="visual-divider fade-in delay-150"></div>
        
        {/* Share with Friends Section - Enhanced with animations */}
        <div className="share-with-friends-container fade-in delay-200">
          <div className="share-with-friends-content">
            <h3 className="share-with-friends-title">
              <span className="title-decoration left">✨</span>
              Share with Friends
              <span className="title-decoration right">✨</span>
            </h3>
            <p className="share-with-friends-text">
              Invite your friends to test their biblical knowledge and grow together in faith!
            </p>
            <div className="share-buttons">
              <button 
                className={`share-button share-facebook ${activeShareButton === 'facebook' ? 'active' : ''}`}
                onClick={() => handleShare('facebook')}
                aria-label="Share on Facebook"
              >
                <span className="share-icon"><FacebookIcon size={20} /></span>
                <span>Facebook</span>
              </button>
              <button 
                className={`share-button share-twitter ${activeShareButton === 'twitter' ? 'active' : ''}`}
                onClick={() => handleShare('twitter')}
                aria-label="Share on Twitter"
              >
                <span className="share-icon"><TwitterIcon size={20} /></span>
                <span>Twitter</span>
              </button>
              <button 
                className={`share-button share-whatsapp ${activeShareButton === 'whatsapp' ? 'active' : ''}`}
                onClick={() => handleShare('whatsapp')}
                aria-label="Share on WhatsApp"
              >
                <span className="share-icon"><WhatsAppIcon size={20} /></span>
                <span>WhatsApp</span>
              </button>
              <button 
                className={`share-button share-email ${activeShareButton === 'email' ? 'active' : ''}`}
                onClick={() => handleShare('email')}
                aria-label="Share via Email"
              >
                <span className="share-icon"><EmailIcon size={20} /></span>
                <span>Email</span>
              </button>
              <button 
                className={`share-button share-copy ${activeShareButton === 'copy' ? 'active' : ''}`}
                onClick={() => handleShare('copy')}
                aria-label="Copy link to clipboard"
              >
                <span className="share-icon"><LinkIcon size={20} /></span>
                <span className={copySuccess ? 'copied-text' : ''}>
                  {copySuccess ? 'Copied!' : 'Copy Link'}
                </span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="fade-in delay-300">
          <BackendStatus />
        </div>
      </div>
    </div>
  );
};

export default HomePage; 